/**
 * Ported VERBATIM from `apr_template/src/pages/ai-analysis/KnowledgeLibrary.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import PageHeader from '../compat/PageHeader';
import apiService from '../compat/api';
import HelpPanel from '../compat/HelpPanel';
import './KnowledgeLibrary.css';
import type { Help } from '../compat/helpTypes';
import {
  type KnowledgeFile,
  type KnowledgeFileDetail,
  listKnowledgeFiles,
  uploadKnowledgeFiles,
  downloadKnowledgeFile,
  getKnowledgeFile,
  updateKnowledgeFile,
  deleteKnowledgeFile,
} from '../compat/aiKnowledgeApi';

const KnowledgeLibraryHelp: Help = {
  title: 'Knowledge Library',
  icon: '📚',
  purpose: 'A shared library of source documents — contracts, usage reports, vendor proposals, interview notes — that the strategic analysis agents read as context when producing recommendations.',
  doHere: [
    'Click <b>Upload Files</b> to add documents; drag-and-drop or browse (PDF, DOCX, XLSX, CSV, TXT, MD).',
    'Set each file\'s <b>Scope</b> to a specific application or leave it Portfolio-wide, and add a description and tags.',
    'Filter the list by filename search, scope, or tag to find documents quickly.',
    'Use the row actions to <b>download</b>, <b>edit</b> (scope/description/tags), or <b>delete</b> a file.',
  ],
  fits: 'Knowledge Library',
  firstTime: 'Upload a few contracts or usage reports before launching a run so the agents have context to work from.',
  sections: [
    {
      type: 'defs',
      label: 'Scope',
      items: [
        { term: 'Portfolio', color: '#0d47a1', def: 'Applies to the whole portfolio; every analysis run can reference it.' },
        { term: 'Application', color: '#202124', def: 'Scoped to one application; child apps also inherit documents from their parent.' },
        { term: 'inherited', color: '#0d47a1', def: 'Shown when a file is scoped to the selected app\'s parent and surfaced by inheritance.' },
      ],
    },
  ],
  links: [
    { href: '/apr/application-evaluation/disposition-intelligence/run-analysis', label: 'Run Analysis' },
    { href: '/apr/application-evaluation/disposition-intelligence/analysis-results', label: 'Analysis Results' },
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ─── Upload Modal ─────────────────────────────────────────────────────────────

interface Application {
  id: string;
  name: string;
  parentApplicationId?: string | null;
}

interface UploadModalProps {
  applications: Application[];
  /** The library category this upload lands in ('diagrams' | 'licensing'). */
  category?: string;
  onClose: () => void;
  onUploaded: (files: KnowledgeFile[]) => void;
}

const UploadModal: React.FC<UploadModalProps> = ({ applications, category, onClose, onUploaded }) => {
  const [dragOver, setDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [applicationId, setApplicationId] = useState<string>('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ALLOWED = ['.pdf', '.docx', '.xlsx', '.pptx', '.ppt', '.csv', '.txt', '.md'];

  const addFiles = (incoming: File[]) => {
    const valid = incoming.filter((f) => {
      const ext = f.name.slice(f.name.lastIndexOf('.')).toLowerCase();
      return ALLOWED.includes(ext);
    });
    const invalid = incoming.filter((f) => {
      const ext = f.name.slice(f.name.lastIndexOf('.')).toLowerCase();
      return !ALLOWED.includes(ext);
    });
    if (invalid.length > 0) {
      setError(`Skipped unsupported file(s): ${invalid.map((f) => f.name).join(', ')}`);
    } else {
      setError(null);
    }
    setSelectedFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name));
      return [...prev, ...valid.filter((f) => !existing.has(f.name))];
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(Array.from(e.target.files));
  };

  const removeFile = (name: string) => {
    setSelectedFiles((prev) => prev.filter((f) => f.name !== name));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      setError('Please select at least one file.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      selectedFiles.forEach((f) => fd.append('files', f));
      if (applicationId) fd.append('applicationId', applicationId);
      if (category) fd.append('category', category);
      fd.append('description', description);
      fd.append('tags', tags);
      const uploaded = await uploadKnowledgeFiles(fd);
      onUploaded(uploaded);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rd-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rd-modal" style={{ maxWidth: 560, width: '100%' }}>
        <div className="rd-modal-header">
          <h2 className="rd-modal-title">Upload Files</h2>
          <button className="rd-modal-close rd-btn rd-btn-ghost" onClick={onClose} type="button">
            <i className="material-icons">close</i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="rd-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Drag-and-drop zone */}
            <div
              className={`rd-dropzone${dragOver ? ' rd-dropzone--over' : ''}`}
              style={{
                border: '2px dashed var(--rd-border, #d1d5db)',
                borderRadius: 8,
                padding: '32px 16px',
                textAlign: 'center',
                cursor: 'pointer',
                background: dragOver ? 'var(--rd-surface-hover, #f0f9ff)' : 'var(--rd-surface, #fafafa)',
                transition: 'background 0.15s, border-color 0.15s',
                borderColor: dragOver ? 'var(--rd-accent, #2563eb)' : undefined,
              }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
            >
              <i className="material-icons" style={{ fontSize: 40, color: 'var(--rd-muted, #9ca3af)' }}>
                cloud_upload
              </i>
              <p style={{ margin: '8px 0 4px', fontWeight: 500 }}>
                Drag files here or click to browse
              </p>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--rd-muted, #9ca3af)' }}>
                Supported: PDF, DOCX, XLSX, PPTX, PPT, CSV, TXT, MD — multiple files allowed
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.docx,.xlsx,.pptx,.ppt,.csv,.txt,.md"
                style={{ display: 'none' }}
                onChange={handleFileInput}
              />
            </div>

            {/* Selected file list */}
            {selectedFiles.length > 0 && (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {selectedFiles.map((f) => (
                  <li
                    key={f.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '4px 8px',
                      background: 'var(--rd-surface-alt, #f3f4f6)',
                      borderRadius: 4,
                      fontSize: 13,
                    }}
                  >
                    <i className="material-icons" style={{ fontSize: 16, color: 'var(--rd-muted, #6b7280)' }}>
                      insert_drive_file
                    </i>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.name}
                    </span>
                    <span style={{ color: 'var(--rd-muted, #9ca3af)', flexShrink: 0 }}>
                      {formatBytes(f.size)}
                    </span>
                    <button
                      type="button"
                      className="rd-btn rd-btn-ghost"
                      style={{ padding: '2px 4px', minWidth: 0 }}
                      onClick={(e) => { e.stopPropagation(); removeFile(f.name); }}
                    >
                      <i className="material-icons" style={{ fontSize: 16 }}>close</i>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Scope */}
            <div className="rd-form-group">
              <label className="rd-label" htmlFor="kl-scope">Scope</label>
              <select
                id="kl-scope"
                className="rd-select"
                value={applicationId}
                onChange={(e) => setApplicationId(e.target.value)}
              >
                <option value="">Portfolio-wide</option>
                {applications.map((app) => (
                  <option key={app.id} value={app.id}>{app.name}</option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="rd-form-group">
              <label className="rd-label" htmlFor="kl-desc">Description</label>
              <textarea
                id="kl-desc"
                className="rd-textarea"
                rows={2}
                placeholder="Brief note about these files…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            {/* Tags */}
            <div className="rd-form-group">
              <label className="rd-label" htmlFor="kl-tags">Tags</label>
              <input
                id="kl-tags"
                type="text"
                className="rd-input"
                placeholder="contract, usage-report, vendor-quote"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
              />
              <span className="rd-form-hint">Comma-separated</span>
            </div>

            {error && (
              <p className="rd-error-text" style={{ margin: 0 }}>{error}</p>
            )}
          </div>

          <div className="rd-modal-footer">
            <button type="button" className="rd-btn rd-btn-ghost" onClick={onClose} disabled={uploading}>
              Cancel
            </button>
            <button type="submit" className="rd-btn rd-btn-primary" disabled={uploading || selectedFiles.length === 0}>
              {uploading ? 'Uploading…' : `Upload ${selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Preview Panel ────────────────────────────────────────────────────────────

/**
 * Document preview. Renders the ORIGINAL where the browser can be made to, and falls
 * back to the extracted text where it can't:
 *
 *   Word  → docx-preview renders real docx layout
 *   Excel → SheetJS (already a dependency) renders the sheets
 *   PDF   → the browser's own viewer (Chrome/Safari render PDF natively)
 *   PowerPoint → extracted text for now. No browser can render pptx, and there is no
 *     usable client-side renderer; true PowerPoint preview needs server-side
 *     conversion (LibreOffice → PDF), which is a deliberate infra decision — see
 *     "PowerPoint preview" in the backlog. Until then the text is honest about what
 *     the AI reads.
 *
 * The text fallback is never wasted: extracted text is the ONLY thing the agents read,
 * so it shows exactly what the AI will work from, and makes a failed extraction
 * obvious before a run is spent on it.
 */
type PreviewKind = 'word' | 'excel' | 'pdf' | 'text';

function previewKind(f?: KnowledgeFileDetail | null): PreviewKind {
  const name = (f?.file_name || '').toLowerCase();
  if (name.endsWith('.docx')) return 'word';
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) return 'excel';
  if (name.endsWith('.pdf')) return 'pdf';
  return 'text'; // pptx/ppt, csv, txt, md — and anything unrecognised
}

const PreviewPanel: React.FC<{ fileId: string; onClose: () => void; onDownload: (f: KnowledgeFile) => void }> = ({ fileId, onClose, onDownload }) => {
  const [file, setFile] = useState<KnowledgeFileDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [rendering, setRendering] = useState(false);
  const [renderErr, setRenderErr] = useState('');
  const [excelHtml, setExcelHtml] = useState<string>('');
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const docxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    setLoading(true); setErr('');
    getKnowledgeFile(fileId)
      .then((f) => { if (live) setFile(f); })
      .catch((e: any) => { if (live) setErr(e?.response?.data?.error || e?.message || 'Could not load the document.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [fileId]);

  const kind = previewKind(file);

  // Fetch the original bytes and render them, for the formats we can show natively.
  useEffect(() => {
    if (!file || kind === 'text') return;
    let live = true;
    let objectUrl = '';
    setRendering(true); setRenderErr('');
    (async () => {
      try {
        const { blob } = await downloadKnowledgeFile(file.id);
        if (!live) return;
        if (kind === 'pdf') {
          objectUrl = URL.createObjectURL(blob);
          setPdfUrl(objectUrl);
        } else if (kind === 'word') {
          const { renderAsync } = await import('docx-preview');
          if (!live || !docxRef.current) return;
          docxRef.current.innerHTML = '';
          await renderAsync(await blob.arrayBuffer(), docxRef.current, undefined, {
            className: 'docx', inWrapper: true, breakPages: true, ignoreWidth: false,
          });
        } else if (kind === 'excel') {
          const XLSX = await import('xlsx');
          const wb = XLSX.read(await blob.arrayBuffer(), { type: 'array' });
          const parts = wb.SheetNames.map((n) =>
            `<div style="font-weight:700;color:#37474f;margin:12px 0 6px">${n}</div>${XLSX.utils.sheet_to_html(wb.Sheets[n]!)}`);
          if (live) setExcelHtml(parts.join(''));
        }
      } catch (e: any) {
        if (live) setRenderErr(e?.message || 'Could not render this document — use Original to open it.');
      } finally {
        if (live) setRendering(false);
      }
    })();
    return () => { live = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [file, kind]);

  const text = file?.extracted_text || '';
  const ok = file?.text_extraction_status === 'ok' && text.trim().length > 0;
  const isPpt = /\.pptx?$/i.test(file?.file_name || '');

  return (
    <>
      {/* 1500/1501 — above the shell's app bar (1201) and the fixed "How this works"
          button (1450), which otherwise cover the panel's title row and close button.
          Same levels as the help drawer. */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1500 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(900px, 96vw)', background: '#fff',
        zIndex: 1501, boxShadow: '-8px 0 30px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #eceff1', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#212121', wordBreak: 'break-word' }}>
              {file?.file_name || 'Document'}
            </div>
            <div style={{ fontSize: 12, color: '#78909c', marginTop: 3 }}>
              {file ? `${Math.round((file.size_bytes || 0) / 1024).toLocaleString()} KB` : ''}
              {kind === "text" && ok && ` · ${text.length.toLocaleString()} characters of text the AI reads`}
              {file?.uploaded_at && ` · uploaded ${new Date(file.uploaded_at).toLocaleString()}`}
            </div>
          </div>
          {file && (
            <button className="rd-btn rd-btn-ghost" onClick={() => onDownload(file)} style={{ fontSize: 12, flexShrink: 0 }}>
              ⬇️ Original
            </button>
          )}
          {/* Close — upper-right red box, same as the Application Master Data drawer. */}
          <button onClick={onClose} aria-label="Close" title="Close"
            style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 9, border: '1.5px solid #dc2626', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="material-icons" style={{ fontSize: 20 }}>close</i>
          </button>
        </div>

        <div style={{ padding: '14px 20px', overflowY: 'auto', flex: 1, background: kind === 'word' ? '#f1f3f5' : undefined }}>
          {err && <div style={{ fontSize: 13, color: '#c62828' }}>{err}</div>}
          {loading && <div style={{ color: '#9e9e9e', padding: 20 }}>Loading…</div>}
          {rendering && <div style={{ color: '#9e9e9e', padding: 12 }}>Rendering document…</div>}
          {renderErr && (
            <div style={{ background: '#fff8e1', border: '1px solid #ffe082', color: '#8a6d00', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 10 }}>
              {renderErr}
            </div>
          )}

          {/* Word — real docx layout, paginated */}
          {!loading && kind === 'word' && <div ref={docxRef} />}

          {/* Excel — sheets rendered as tables */}
          {!loading && kind === 'excel' && excelHtml && (
            <div style={{ fontSize: 12.5, overflowX: 'auto' }} dangerouslySetInnerHTML={{ __html: excelHtml }} />
          )}

          {/* PDF — the browser's own viewer */}
          {!loading && kind === 'pdf' && pdfUrl && (
            <iframe title="Document preview" src={pdfUrl} style={{ width: '100%', height: '100%', minHeight: 600, border: 'none' }} />
          )}

          {/* PowerPoint (and anything else) — the text the AI reads */}
          {!loading && kind === 'text' && file && (
            <>
              {isPpt && (
                <div style={{ background: '#eef4ff', border: '1px solid #cfe0f7', color: '#1d3a5f', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, lineHeight: 1.55, marginBottom: 10 }}>
                  <strong>Slide preview is not available at the moment.</strong> This feature will be available soon.
                </div>
              )}
              {!ok && (
                <div style={{ background: '#fff8e1', border: '1px solid #ffe082', color: '#8a6d00', borderRadius: 8, padding: '10px 12px', fontSize: 13, lineHeight: 1.55 }}>
                  <strong>No readable text was extracted from this document.</strong> The AI would see nothing here, so it
                  can't be used for analysis. {file.text_extraction_error ? `Extractor said: ${file.text_extraction_error}. ` : ''}
                  This usually means a scanned or image-only file — re-export it as a text-based Word or PDF and upload again.
                </div>
              )}
              {ok && (
                <>
                  <div style={{ fontSize: 11.5, color: '#78909c', marginBottom: 8, lineHeight: 1.5 }}>
                    This is the text extracted from the document — exactly what the agents read. Formatting, images and
                    layout are not part of it.
                  </div>
                  <div style={{
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12.5, lineHeight: 1.65,
                    color: '#263238', background: '#fafbfc', border: '1px solid #eceff1', borderRadius: 8, padding: '12px 14px',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  }}>
                    {text}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

// ─── Edit Modal ───────────────────────────────────────────────────────────────

interface EditModalProps {
  file: KnowledgeFile;
  applications: Application[];
  onClose: () => void;
  onSaved: (file: KnowledgeFile) => void;
}

const EditModal: React.FC<EditModalProps> = ({ file, applications, onClose, onSaved }) => {
  const [description, setDescription] = useState(file.description || '');
  const [tags, setTags] = useState((file.tags || []).join(', '));
  const [applicationId, setApplicationId] = useState<string>(file.application_id || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const tagsArray = tags
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
      const updated = await updateKnowledgeFile(file.id, {
        description,
        tags: tagsArray,
        application_id: applicationId === '' ? null : applicationId,
      });
      onSaved(updated);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rd-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rd-modal" style={{ maxWidth: 480, width: '100%' }}>
        <div className="rd-modal-header">
          <h2 className="rd-modal-title">Edit File</h2>
          <button className="rd-modal-close rd-btn rd-btn-ghost" onClick={onClose} type="button">
            <i className="material-icons">close</i>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="rd-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, fontWeight: 500, fontSize: 14 }}>{file.file_name}</p>

            <div className="rd-form-group">
              <label className="rd-label" htmlFor="edit-scope">Scope</label>
              <select
                id="edit-scope"
                className="rd-select"
                value={applicationId}
                onChange={(e) => setApplicationId(e.target.value)}
              >
                <option value="">Portfolio-wide</option>
                {applications.map((app) => (
                  <option key={app.id} value={app.id}>{app.name}</option>
                ))}
              </select>
            </div>

            <div className="rd-form-group">
              <label className="rd-label" htmlFor="edit-desc">Description</label>
              <textarea
                id="edit-desc"
                className="rd-textarea"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="rd-form-group">
              <label className="rd-label" htmlFor="edit-tags">Tags</label>
              <input
                id="edit-tags"
                type="text"
                className="rd-input"
                placeholder="contract, usage-report"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
              />
              <span className="rd-form-hint">Comma-separated</span>
            </div>

            {error && <p className="rd-error-text" style={{ margin: 0 }}>{error}</p>}
          </div>
          <div className="rd-modal-footer">
            <button type="button" className="rd-btn rd-btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="rd-btn rd-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

interface DeleteModalProps {
  file: KnowledgeFile;
  onClose: () => void;
  onDeleted: (id: string) => void;
}

const DeleteModal: React.FC<DeleteModalProps> = ({ file, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await deleteKnowledgeFile(file.id);
      onDeleted(file.id);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Delete failed');
      setDeleting(false);
    }
  };

  return (
    <div className="rd-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rd-modal" style={{ maxWidth: 400, width: '100%' }}>
        <div className="rd-modal-header">
          <h2 className="rd-modal-title">Delete File</h2>
          <button className="rd-modal-close rd-btn rd-btn-ghost" onClick={onClose} type="button">
            <i className="material-icons">close</i>
          </button>
        </div>
        <div className="rd-modal-body">
          <p>
            Are you sure you want to delete <strong>{file.file_name}</strong>?
            This action cannot be undone.
          </p>
          {error && <p className="rd-error-text">{error}</p>}
        </div>
        <div className="rd-modal-footer">
          <button type="button" className="rd-btn rd-btn-ghost" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button
            type="button"
            className="rd-btn rd-btn-danger"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

// The two document categories the library is split into. The URL param (:category)
// selects one; uploads land in it and the list is filtered to it.
const CATEGORY_META: Record<string, { label: string; subtitle: string }> = {
  diagrams: {
    label: 'Architecture & Process Diagrams',
    subtitle: 'Architecture diagrams, integration flows, and process maps the analysis will reference.',
  },
  licensing: {
    label: 'Licensing',
    subtitle: 'Contracts, licensing agreements, usage reports, and vendor proposals for licence analysis.',
  },
};

/**
 * `categoryProp` is the platform's addition. The source reads the category from a
 * `:category` route param; the platform's APR routes are positional (`/apr/:a/:b/:c`),
 * so the module passes it in instead. The `useParams` fallback is the source's, kept
 * intact — nothing else about the component changed.
 */
const KnowledgeLibrary: React.FC<{ category?: string }> = ({ category: categoryProp }) => {
  const { category: categoryParam } = useParams<{ category?: string }>();
  const requested = categoryProp ?? categoryParam;
  const category = requested && CATEGORY_META[requested] ? requested : 'diagrams';
  const meta = CATEGORY_META[category];
  const [files, setFiles] = useState<KnowledgeFile[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Modals
  const [showUpload, setShowUpload] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [editFile, setEditFile] = useState<KnowledgeFile | null>(null);
  const [deleteFile, setDeleteFile] = useState<KnowledgeFile | null>(null);

  // Filters
  const [scopeFilter, setScopeFilter] = useState<string>('all');
  const [tagFilter, setTagFilter] = useState('');
  const [search, setSearch] = useState('');

  // ── Screen lock ─────────────────────────────────────────────────────────────
  // The page never scrolls: header and filters stay put and only the file list
  // scrolls. `.page`'s own `calc(100vh - 120px)` leaves the window scrolling under
  // the fixed "How this works" button, so the height is measured instead — the
  // same way ProcessHierarchy and LicensingResults do it. Callback ref, because
  // the page mounts before the measurement effect needs it.
  const [pageEl, setPageEl] = useState<HTMLDivElement | null>(null);
  const [pageH, setPageH] = useState<number | null>(null);
  useEffect(() => {
    if (!pageEl) return;
    const main = pageEl.closest('main');
    const measure = () => {
      const top = pageEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setPageH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [pageEl]);

  // ── Load data ───────────────────────────────────────────────────────────────
  const loadFiles = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [fetchedFiles, fetchedApps] = await Promise.all([
        listKnowledgeFiles({ category }),
        apiService.getApplications(),
      ]);
      setFiles(fetchedFiles);
      setApplications(
        (fetchedApps as any[]).map((a: any) => ({
          id: a.id,
          name: a.name,
          parentApplicationId: a.parentApplicationId ?? null,
        }))
      );
    } catch (err: any) {
      setLoadError(err.response?.data?.error || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleUploaded = (uploaded: KnowledgeFile[]) => {
    setFiles((prev) => [...uploaded, ...prev]);
  };

  const handleSaved = (updated: KnowledgeFile) => {
    setFiles((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
  };

  const handleDeleted = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleDownload = async (file: KnowledgeFile) => {
    try {
      const { blob, filename } = await downloadKnowledgeFile(file.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.response?.data?.error || err.message || 'Download failed');
    }
  };

  // ── Filtering ───────────────────────────────────────────────────────────────
  // When a specific (child) application is selected, also surface documents
  // inherited from its parent application.
  const selectedApp = applications.find((a) => a.id === scopeFilter);
  const selectedParentId = selectedApp?.parentApplicationId || null;

  const filteredFiles = files.filter((f) => {
    if (scopeFilter === 'portfolio' && f.application_id !== null && f.application_id !== undefined) return false;
    if (scopeFilter !== 'all' && scopeFilter !== 'portfolio') {
      const matchesScope =
        f.application_id === scopeFilter ||
        (selectedParentId !== null && f.application_id === selectedParentId);
      if (!matchesScope) return false;
    }

    if (tagFilter.trim()) {
      const tq = tagFilter.trim().toLowerCase();
      if (!(f.tags || []).some((t) => t.toLowerCase().includes(tq))) return false;
    }

    if (search.trim()) {
      const sq = search.trim().toLowerCase();
      if (!f.file_name.toLowerCase().includes(sq)) return false;
    }

    return true;
  });

  // ── Collect all unique tags for the tag filter dropdown ─────────────────────
  const allTags = Array.from(new Set(files.flatMap((f) => f.tags || []))).sort();

  // ── Scope label helper ───────────────────────────────────────────────────────
  const scopeLabel = (f: KnowledgeFile) =>
    f.application_id ? (f.application_name || f.application_id) : 'Portfolio';

  return (
    <div
      ref={setPageEl}
      className="page kl-locked-page"
      style={{ height: pageH ?? undefined, minHeight: 0, overflow: 'hidden' }}
    >
      <HelpPanel help={KnowledgeLibraryHelp} />
      <PageHeader
        title={`Knowledge Library · ${meta!.label}`}
        subtitle={meta!.subtitle}
        actions={
          <button
            type="button"
            className="rd-btn rd-btn-primary"
            onClick={() => setShowUpload(true)}
          >
            <i className="material-icons" style={{ fontSize: 18 }}>upload</i>
            Upload Files
          </button>
        }
      />

      {/* Filter bar */}
      <div
        className="rd-filter-bar"
        style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20, alignItems: 'center' }}
      >
        {/* Search */}
        <div style={{ position: 'relative', flex: '1 1 180px', minWidth: 140 }}>
          <i
            className="material-icons"
            style={{
              position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
              fontSize: 18, color: 'var(--rd-muted, #9ca3af)', pointerEvents: 'none',
            }}
          >
            search
          </i>
          <input
            type="text"
            className="rd-input"
            style={{ paddingLeft: 32 }}
            placeholder="Search by filename…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Scope filter */}
        <select
          className="rd-select"
          style={{ flex: '0 0 auto', minWidth: 160 }}
          value={scopeFilter}
          onChange={(e) => setScopeFilter(e.target.value)}
        >
          <option value="all">All scopes</option>
          <option value="portfolio">Portfolio-wide</option>
          {applications.map((app) => (
            <option key={app.id} value={app.id}>{app.name}</option>
          ))}
        </select>

        {/* Tag filter */}
        {allTags.length > 0 ? (
          <select
            className="rd-select"
            style={{ flex: '0 0 auto', minWidth: 140 }}
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
          >
            <option value="">All tags</option>
            {allTags.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            className="rd-input"
            style={{ flex: '0 0 auto', minWidth: 140 }}
            placeholder="Filter by tag…"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
          />
        )}

        {(search || scopeFilter !== 'all' || tagFilter) && (
          <button
            type="button"
            className="rd-btn rd-btn-ghost"
            onClick={() => { setSearch(''); setScopeFilter('all'); setTagFilter(''); }}
          >
            <i className="material-icons" style={{ fontSize: 16 }}>clear</i>
            Clear
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="rd-loading" style={{ padding: '48px 0', textAlign: 'center' }}>
          <i className="material-icons rd-spin" style={{ fontSize: 32 }}>sync</i>
          <p>Loading files…</p>
        </div>
      ) : loadError ? (
        <div className="rd-error-banner" style={{ padding: 16 }}>
          <i className="material-icons">error</i> {loadError}
        </div>
      ) : filteredFiles.length === 0 ? (
        <div className="rd-empty" style={{ padding: '48px 20px', textAlign: 'center' }}>
          <div className="rd-empty-icon">
            <i className="material-icons" style={{ fontSize: 48, color: 'var(--rd-muted, #9ca3af)' }}>
              folder_open
            </i>
          </div>
          {files.length === 0 ? (
            <>
              <h2 className="rd-empty-title">No files yet</h2>
              <p className="rd-empty-body">
                Upload your first one to give the agents context.
              </p>
              <button
                type="button"
                className="rd-btn rd-btn-primary"
                onClick={() => setShowUpload(true)}
                style={{ marginTop: 12 }}
              >
                <i className="material-icons" style={{ fontSize: 18 }}>upload</i>
                Upload Files
              </button>
            </>
          ) : (
            <>
              <h2 className="rd-empty-title">No matching files</h2>
              <p className="rd-empty-body">Try adjusting your filters.</p>
            </>
          )}
        </div>
      ) : (
        <div className="rd-table-wrapper kl-scroll">
          <table className="rd-table">
            <thead>
              <tr>
                <th>File name</th>
                <th>Scope</th>
                <th>Tags</th>
                <th>Uploaded by</th>
                <th>Uploaded at</th>
                <th>Size</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFiles.map((f) => (
                <tr key={f.id}>
                  <td>
                    <span style={{ fontWeight: 500 }}>{f.file_name}</span>
                    {f.description && (
                      <span
                        style={{ display: 'block', fontSize: 12, color: 'var(--rd-muted, #6b7280)', marginTop: 2 }}
                      >
                        {f.description}
                      </span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`rd-badge ${f.application_id ? 'rd-badge-neutral' : 'rd-badge-info'}`}
                      style={{ fontSize: 12 }}
                    >
                      {scopeLabel(f)}
                    </span>
                    {selectedParentId !== null && f.application_id === selectedParentId && (
                      <span
                        style={{
                          marginLeft: 6, padding: '1px 6px', fontSize: 10, fontWeight: 600,
                          background: '#e3f2fd', color: '#1565c0', borderRadius: 10,
                        }}
                        title={`Inherited from parent application ${scopeLabel(f)}`}
                      >
                        inherited
                      </span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(f.tags || []).length === 0 ? (
                        <span style={{ color: 'var(--rd-muted, #9ca3af)', fontSize: 12 }}>—</span>
                      ) : (
                        (f.tags || []).map((tag) => (
                          <span key={tag} className="rd-tag rd-tag-sm" style={{ fontSize: 11 }}>
                            {tag}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td style={{ fontSize: 13 }}>{f.uploaded_by_name || '—'}</td>
                  <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{formatDate(f.uploaded_at)}</td>
                  <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{formatBytes(f.size_bytes)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
                      <button
                        type="button"
                        className="action-btn view-btn"
                        title="Preview the text the AI reads from this document"
                        onClick={() => setPreviewId(f.id)}
                        style={{ padding: '4px 6px', backgroundColor: 'transparent', color: '#5e35b1', border: '1px solid #5e35b1', borderRadius: 4, fontSize: 14, cursor: 'pointer', minWidth: 'auto' }}
                      >
                        👁️
                      </button>
                      <button
                        type="button"
                        className="action-btn view-btn"
                        title="Download the original file"
                        onClick={() => handleDownload(f)}
                        style={{ padding: '4px 6px', backgroundColor: 'transparent', color: '#1976d2', border: '1px solid #1976d2', borderRadius: 4, fontSize: 14, cursor: 'pointer', minWidth: 'auto' }}
                      >
                        ⬇️
                      </button>
                      <button
                        type="button"
                        className="action-btn edit-btn"
                        title="Edit"
                        onClick={() => setEditFile(f)}
                        style={{ padding: '4px 6px', backgroundColor: 'transparent', color: '#1976d2', border: '1px solid #1976d2', borderRadius: 4, fontSize: 14, cursor: 'pointer', minWidth: 'auto' }}
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        className="action-btn delete-btn"
                        title="Delete"
                        onClick={() => setDeleteFile(f)}
                        style={{ padding: '4px 6px', backgroundColor: 'transparent', color: '#d32f2f', border: '1px solid #d32f2f', borderRadius: 4, fontSize: 14, cursor: 'pointer', minWidth: 'auto' }}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showUpload && (
        <UploadModal
          category={category}
          applications={applications}
          onClose={() => setShowUpload(false)}
          onUploaded={handleUploaded}
        />
      )}
      {previewId && (
        <PreviewPanel fileId={previewId} onClose={() => setPreviewId(null)} onDownload={handleDownload} />
      )}

      {editFile && (
        <EditModal
          file={editFile}
          applications={applications}
          onClose={() => setEditFile(null)}
          onSaved={handleSaved}
        />
      )}
      {deleteFile && (
        <DeleteModal
          file={deleteFile}
          onClose={() => setDeleteFile(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
};

export default KnowledgeLibrary;
